import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Phone, MessageCircle, ChevronDown, ChevronUp, ChevronRight, FlaskConical, Scan, HeartPulse, BrainCircuit, Wind, ArrowLeft, CheckCircle, ExternalLink, Heart, Brain, Activity, Microscope, Stethoscope } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';
import { publicAPI } from '../services/api';
import { companyInfo } from '../data/mock';
import { Button } from '../components/ui/button';

const iconMap = {
  FlaskConical: FlaskConical,
  Scan: Scan,
  HeartPulse: HeartPulse,
  BrainCircuit: BrainCircuit,
  Wind: Wind,
  Heart: Heart,
  Brain: Brain,
  Activity: Activity,
  Microscope: Microscope,
  Stethoscope: Stethoscope,
  // Map service names to appropriate icons
  Cardiology: Heart,
  Electrophysiology: Brain
};

// Animated Service Icon Component - matching homepage
const AnimatedServiceIcon = ({ icon: IconName, serviceName = "", className = "" }) => {
  const Icon = iconMap[serviceName] || iconMap[IconName] || FlaskConical;
  
  return (
    <div className={`relative ${className}`}>
      {/* Pulsing background ring */}
      <div className="absolute inset-0 rounded-xl bg-[#007897]/20 animate-ping opacity-20" style={{ animationDuration: '2s' }} />
      {/* Rotating gradient border */}
      <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-[#007897] via-[#00a0af] to-[#3949ab] opacity-30 animate-spin" style={{ animationDuration: '8s' }} />
      {/* Icon container */}
      <div className="relative w-full h-full bg-gradient-to-br from-[#007897]/10 to-[#3949ab]/10 rounded-xl flex items-center justify-center group-hover:from-[#007897] group-hover:to-[#3949ab] transition-all duration-300">
        <Icon className="w-6 h-6 text-[#007897] group-hover:text-white transition-colors animate-pulse" style={{ animationDuration: '2s' }} />
      </div>
    </div>
  );
};

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [allServices, setAllServices] = useState([]);
  const [testDetailSlugs, setTestDetailSlugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSubService, setExpandedSubService] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Try to get service by slug first
        try {
          const response = await publicAPI.getServiceBySlug(slug);
          setService(response.data);
        } catch (err) {
          // If not found by slug, get all services and find by slug match
          const allRes = await publicAPI.getServices();
          setAllServices(allRes.data);
          const found = allRes.data.find(s => 
            s.slug === slug || 
            s.name.toLowerCase().replace(/\s+/g, '-') === slug
          );
          if (found) {
            setService(found);
          } else {
            setError('Service not found');
          }
        }
        
        // Get all services for related section
        const allRes = await publicAPI.getServices();
        setAllServices(allRes.data);
        
        // Get test detail slugs to know which tests have detail pages
        try {
          const testDetailsRes = await publicAPI.getTestDetails();
          setTestDetailSlugs(testDetailsRes.data.map(t => t.slug));
        } catch (err) {
          console.log('No test details available');
        }
      } catch (err) {
        console.error('Error fetching service:', err);
        setError('Failed to load service details');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  // Helper function to generate test slug from name
  const generateTestSlug = (testName) => {
    return testName.toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[()]/g, '')
      .replace(/--+/g, '-');
  };

  // Check if a test has a detail page
  const hasDetailPage = (testName) => {
    const testSlug = generateTestSlug(testName);
    return testDetailSlugs.some(slug => 
      slug === testSlug || 
      testSlug.includes(slug) || 
      slug.includes(testSlug.split('-')[0])
    );
  };

  // Get the matching test detail slug
  const getTestDetailSlug = (testName) => {
    const testSlug = generateTestSlug(testName);
    return testDetailSlugs.find(slug => 
      slug === testSlug || 
      testSlug.includes(slug) || 
      slug.includes(testSlug.split('-')[0])
    ) || testSlug;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-32 pb-16 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#007897]"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-32 pb-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Service Not Found</h1>
            <p className="text-gray-600 mb-8">The service you are looking for does not exist or has been removed.</p>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 bg-[#007897] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#006680] transition-colors"
            >
              <ArrowLeft size={18} /> Back to Services
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const Icon = iconMap[service.icon] || FlaskConical;
  const otherServices = allServices.filter(s => s.slug !== slug && s.id !== service.id);

  // Calculate total tests count
  const totalTests = service.sub_services 
    ? service.sub_services.reduce((acc, sub) => acc + (sub.tests?.length || 0), 0)
    : (service.tests?.length || 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#007897] to-[#005a73]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-2 text-sm text-white/70 mb-6">
            <Link to="/" className="hover:text-white">Home</Link>
            <span>/</span>
            <Link to="/services" className="hover:text-white">Services</Link>
            <span>/</span>
            <span className="text-white">{service.name}</span>
          </div>
          
          <div className="text-center text-white">
            <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Icon className="w-10 h-10" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{service.name}</h1>
            <p className="text-white/80 max-w-2xl mx-auto mb-6 text-lg">
              {service.description}
            </p>
            <div className="flex items-center justify-center gap-6 text-sm">
              <span className="bg-white/20 px-4 py-2 rounded-full">
                {service.sub_services?.length || 0} Categories
              </span>
              <span className="bg-white/20 px-4 py-2 rounded-full">
                {totalTests} Tests Available
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Sub Services Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our {service.name}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Explore our comprehensive range of tests and services under {service.name}.
            </p>
          </div>

          {service.sub_services && service.sub_services.length > 0 ? (
            <div className="space-y-4">
              {service.sub_services.map((subService, index) => {
                // Always use department icon for subservices
                const DepartmentIcon = iconMap[service.icon] || FlaskConical;
                
                return (
                  <div key={subService.id || index} className="bg-gray-50 rounded-2xl overflow-hidden">
                    <button
                      onClick={() => setExpandedSubService(expandedSubService === index ? null : index)}
                      className="w-full p-4 sm:p-5 flex items-start justify-between text-left hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-start gap-3 sm:gap-4 flex-1">
                        {/* Icon container - Use department icon */}
                        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden flex-shrink-0">
                          <div className="w-full h-full bg-gradient-to-br from-[#007897]/10 to-[#3949ab]/10 flex items-center justify-center">
                            <DepartmentIcon className="w-8 h-8 text-[#007897]" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base sm:text-lg font-semibold text-gray-900">{subService.name}</h3>
                          <p className="text-gray-600 text-sm mt-1 line-clamp-2">{subService.description}</p>
                          {subService.tests && subService.tests.length > 0 && (
                            <span className="inline-flex items-center gap-1 mt-2 text-xs text-[#007897] font-medium">
                              <DepartmentIcon size={12} />
                              {subService.tests.length} tests available
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 ml-2 sm:ml-4 flex-shrink-0">
                        {expandedSubService === index ? (
                          <ChevronUp size={20} className="text-[#007897]" />
                        ) : (
                          <ChevronDown size={20} className="text-gray-400" />
                        )}
                      </div>
                    </button>

                    {expandedSubService === index && (
                      <div className="px-5 pb-5 border-t border-gray-200 bg-white">
                        <div className="grid md:grid-cols-2 gap-6 mt-5">
                          {/* Left Column - Details */}
                          <div className="space-y-4">
                            {subService.description && (
                              <div>
                                <h4 className="font-semibold text-gray-900 mb-2">About this Test</h4>
                                <p className="text-gray-600 text-sm leading-relaxed">{subService.description}</p>
                              </div>
                            )}
                            
                            {subService.procedure && (
                              <div>
                                <h4 className="font-semibold text-gray-900 mb-2">What happens during the procedure?</h4>
                                <p className="text-gray-600 text-sm leading-relaxed">{subService.procedure}</p>
                              </div>
                            )}
                          </div>
                          
                          {/* Right Column - Instructions */}
                          <div>
                            {subService.instructions && subService.instructions.length > 0 && (
                              <div className="bg-amber-50 p-4 rounded-xl">
                                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                  <span className="w-6 h-6 bg-amber-200 rounded-full flex items-center justify-center text-amber-700 text-xs">!</span>
                                  Patient Instructions
                                </h4>
                                <ul className="space-y-2">
                                  {subService.instructions.map((instruction, idx) => (
                                    <li key={`instruction-${subService.id || subService.name}-${idx}`} className="flex items-start gap-2 text-sm text-gray-700">
                                      <CheckCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                      <span>{instruction}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {/* Contact CTA */}
                            <div className="mt-4 p-4 bg-[#007897]/5 rounded-xl">
                              <p className="text-sm text-gray-700 mb-3">Have questions about this test?</p>
                              <a 
                                href="/contact"
                                className="inline-flex items-center gap-2 bg-[#007897] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#006680] transition-colors"
                              >
                                <Phone size={14} />
                                Contact Us
                              </a>
                            </div>
                          </div>
                        </div>
                        
                        {/* Tests List for Services */}
                        {subService.tests && subService.tests.length > 0 && (
                          <div className="mt-6 pt-6 border-t border-gray-200">
                            <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                              <FlaskConical className="w-5 h-5 text-[#007897]" />
                              Available Tests ({subService.tests.length})
                            </h4>
                            <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                              {subService.tests.map((test, testIdx) => {
                                const testName = typeof test === 'string' ? test : test.name;
                                const testDesc = typeof test === 'object' ? test.description : '';
                                const testDiseases = typeof test === 'object' ? test.diseases : '';
                                const testInstructions = typeof test === 'object' ? test.instructions : '';
                                // Use slug from API if available, otherwise generate from name
                                const testSlug = (typeof test === 'object' && test.slug) 
                                  ? test.slug 
                                  : testName.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').trim();
                                // Show "View Full Test Details" for all tests that have detail pages
                                const hideDetailLink = ['fibrotouch'].includes(subService.slug);
                                
                                return (
                                  <details 
                                    key={test.id || `test-${subService.id || subService.name}-${testIdx}`}
                                    className="bg-gray-50 rounded-lg border border-gray-100 hover:border-[#007897]/30 transition-colors group"
                                  >
                                    <summary className="p-3 cursor-pointer list-none flex items-start gap-2 hover:bg-gray-100 rounded-lg">
                                      <CheckCircle className="w-4 h-4 text-[#007897] mt-0.5 flex-shrink-0" />
                                      <div className="flex-1 min-w-0">
                                        <p className="font-medium text-gray-900 text-sm leading-tight">{testName}</p>
                                        {testDesc && (
                                          <p className="text-xs text-gray-500 mt-1 line-clamp-2 group-open:hidden">{testDesc}</p>
                                        )}
                                      </div>
                                      <ChevronDown className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-180 flex-shrink-0" />
                                    </summary>
                                    <div className="px-3 pb-3 pt-0 border-t border-gray-100">
                                      {testDesc && (
                                        <div className="mt-3">
                                          <h5 className="text-xs font-semibold text-[#007897] uppercase tracking-wide mb-1">About the Test</h5>
                                          <p className="text-sm text-gray-600">{testDesc}</p>
                                        </div>
                                      )}
                                      {testDiseases && (
                                        <div className="mt-3">
                                          <h5 className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-1">Conditions Detected</h5>
                                          <p className="text-sm text-gray-600">{testDiseases}</p>
                                        </div>
                                      )}
                                      {testInstructions && (
                                        <div className="mt-3 bg-amber-50 p-2 rounded-lg">
                                          <h5 className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1">Patient Instructions</h5>
                                          <p className="text-sm text-gray-700">{testInstructions}</p>
                                        </div>
                                      )}
                                      {!hideDetailLink && (
                                        <div className="mt-4">
                                          <Link 
                                            to={`/tests/${testSlug}`}
                                            className="inline-flex items-center gap-1 text-sm font-medium text-[#007897] hover:text-[#006680] transition-colors"
                                          >
                                            View Full Test Details
                                            <ChevronRight className="w-4 h-4" />
                                          </Link>
                                        </div>
                                      )}
                                    </div>
                                  </details>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : service.tests && service.tests.length > 0 ? (
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
              {service.tests.map((test, index) => {
                const testName = typeof test === 'string' ? test : test.name;
                const hasDetail = hasDetailPage(testName);
                const testSlug = getTestDetailSlug(testName);
                
                const TestCard = (
                  <div
                    className={`bg-gray-50 p-3 rounded-lg transition-all ${
                      hasDetail 
                        ? 'hover:bg-[#007897]/5 hover:shadow-sm cursor-pointer group' 
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#007897] flex-shrink-0" />
                      <span className={`text-sm text-gray-800 ${hasDetail ? 'group-hover:text-[#007897]' : ''}`}>
                        {testName}
                      </span>
                    </div>
                  </div>
                );
                
                return hasDetail ? (
                  <Link key={`item-${index}`} to={`/tests/${testSlug}`}>
                    {TestCard}
                  </Link>
                ) : (
                  <div key={`item-${index}`}>{TestCard}</div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-2xl">
              <p className="text-gray-500">No tests available for this service. Please contact us for more information.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#007897] to-[#005a73] text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Book Your Test?</h2>
          <p className="text-white/80 mb-8">
            Contact us today for accurate and timely diagnostic services. Our team is available to assist you.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/contact">
              <Button size="lg" className="bg-white text-[#007897] hover:bg-gray-100 px-8">
                <Phone size={18} className="mr-2" />
                Contact Us
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Other Services */}
      {otherServices.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Explore Other Services</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {otherServices.slice(0, 4).map((otherService) => {
                return (
                  <Link
                    key={otherService.id}
                    to={`/services/${otherService.slug}`}
                    className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all group"
                  >
                    <AnimatedServiceIcon 
                      icon={otherService.icon} 
                      serviceName={otherService.name?.split(' ').pop()} 
                      className="w-12 h-12 mb-4"
                    />
                    <h3 className="font-semibold text-gray-900 group-hover:text-[#007897] transition-colors">
                      {otherService.name}
                    </h3>
                    <p className="text-gray-600 text-sm mt-2 line-clamp-2">{otherService.description}</p>
                    <p className="text-[#007897] text-sm mt-3 font-medium">
                      {otherService.sub_services?.length || 0} categories
                    </p>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default ServiceDetailPage;
